import { Category, LevelType } from '@domain/composition/composition.d';
import Base from '../composition/Base'
import Light from '../composition/Light';
import { getLightingEffectOrder } from './LightingEffect';

type HierarchyNode = {
    type: Function;  // The class/constructor of the type
    elements: Base[];
    subtypes: HierarchyNode[];
};

// Hierarchically categorizes the elements based on the class polymorphism
export function categorizeElements(categorizeElements: Base[]): HierarchyNode[] {
    const map = new Map<Function, HierarchyNode>();

    function findOrCreateNode(type: Function): HierarchyNode {
        if (!map.has(type)) {
            map.set(type, { type, elements: [], subtypes: [] });
        }
        return map.get(type)!;
    }

    function addToHierarchy(item: Base, type: Function) {
        const node = findOrCreateNode(type);

        // Add element only if the "item" type is exactly "type"
        if (item.constructor === type) {
            node.elements.push(item);
        }

        const baseType = Object.getPrototypeOf(type.prototype)?.constructor;
        if (baseType && baseType !== Object) {
            const parentNode = findOrCreateNode(baseType);
            const existingSubtype = parentNode.subtypes.find(st => st.type === type);
            if (!existingSubtype) {
                parentNode.subtypes.push(node);
            }
            addToHierarchy(item, baseType);  // Recursive call to continue building the hierarchy
        }
    }

    categorizeElements.forEach(item => {
        addToHierarchy(item, item.constructor);
    });

    // Only return root nodes (those not being subtype of another)
    return Array.from(map.values()).filter(node =>
        Object.getPrototypeOf(node.type.prototype)?.constructor === Object
    );
}

export function filterDuplicatedElementsInSubcategories(category: Category) {
    const _category = { ...category }

    const filteredCategory = _category.elements.filter(element => {
        const subcategories = _category.subcategories
        const existInSomeSubcategory = subcategories?.some(subcategory => {
            const elementInSubcategory = subcategory.elements.includes(element)
            return elementInSubcategory
        })

        return !existInSomeSubcategory
    })
    _category.elements = filteredCategory

    // Sort subcategories by order
    _category.subcategories.sort((a, b) => Number(a.order) - Number(b.order))

    return _category;
}

export function categorizeLightsByLightingEffect(lights: Light[]) {
    const luminariesByLightingEffect: Array<Category> = []

    lights.forEach(light => {
        const lightingEffect = light.getLightingEffect();

        let lightEffectName = "";
        let description = {};
        let id;

        const levels: Array<LevelType> = ["lvl1", "lvl2", "lvl3"];

        levels.forEach(level => {
            if (lightingEffect?.[level]?.description["en"]) {
                lightEffectName = lightingEffect[level].description["en"];
                description = lightingEffect[level].description;
                id = lightingEffect[level].id
            }
        });

        const existingCategoryIndex = luminariesByLightingEffect.findIndex(cat => cat.id === id);
        const categoryAlreadyExists = existingCategoryIndex > -1

        if (categoryAlreadyExists) {
            luminariesByLightingEffect[existingCategoryIndex].elements.push(light);
        } else {
            luminariesByLightingEffect.push({
                title: lightEffectName,
                elements: [light],
                order: getLightingEffectOrder(lightingEffect),
                type: "Light",
                description: description,
                id
            });
        }
    });

    return luminariesByLightingEffect
}

export default {
    categorizeElements,
    categorizeLightsByLightingEffect,
    filterDuplicatedElementsInSubcategories
}